import {Component, OnInit} from '@angular/core';
import {InspectionService} from "../../../services/inspection";
import {ActivatedRoute, Router} from "@angular/router";
import {mergeMap, switchMap, tap} from "rxjs/operators";
import {HttpErrorResponse} from "@angular/common/http";
import {User} from "../../../classes/user";
import {Inspection} from "../../../classes/inspection";
import {InspectionOrder} from "../../../classes/inspectionOrder";
import {InspectionOrderService} from "../../../services/inspectionOrder";
import {InspectionDeliveryRequestService} from "../../../services/inspectionDeliveryRequest";
import {combineLatest, forkJoin, from, of} from "rxjs";
import {ChecklistCategoryService} from "../../../services/checklistCategory";
import {ChecklistSubCategoryService} from "../../../services/checklistSubCategory";
import {ChecklistCategory} from "../../../classes/checklistCategory";
import {ChecklistSubCategory} from "../../../classes/checklistSubCategory";
import {InspectionDeliveryRequest} from "../../../classes/inspectionDeliveryRequest";
import {ChecklistVoteService} from "../../../services/checklistVote";
import {AuthService} from "../../../services/auth";
import {saveAs} from "file-saver";
import {DocumentService} from "../../../services/document";
import {Document} from "../../../classes/document";
import {GeneralModalComponent} from "../../../modals/general/general-modal/general-modal.component";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {
    InspectionDocumentUploadComponent
} from "../../../modals/inspection/inspection-document-upload/inspection-document-upload.component";

@Component({
    selector: 'app-inspection-checklist',
    templateUrl: './inspection-checklist.component.html',
    styleUrls: ['./inspection-checklist.component.css']
})
export class InspectionChecklistComponent implements OnInit {
    loadingOverlay = false;
    isButtonEnabled = true;

    // Inspection Data
    user!: User;
    inspection!: Inspection;
    inspectionOrders: Array<InspectionOrder> = [];
    inspectionDeliveryRequestsByOrder: any = {};
    checklistCategories: Array<ChecklistCategory> = [];
    checklistSubCategories: Array<ChecklistSubCategory> = [];

    // Inspection Votes
    checklistVotes: any = {};
    checklistVotesMeanByCategory: any = {};

    // Inspection Documents
    inspectionDocuments: Array<Document> = [];

    constructor(private route: ActivatedRoute,
                private router: Router,
                private modalService: NgbModal,
                private authService: AuthService,
                private inspectionService: InspectionService,
                private inspectionOrderService: InspectionOrderService,
                private inspectionDeliveryRequestService: InspectionDeliveryRequestService,
                private checklistCategoryService: ChecklistCategoryService,
                private checklistSubCategoryService: ChecklistSubCategoryService,
                private checklistVoteService: ChecklistVoteService,
                private documentService: DocumentService) {
    }

    ngOnInit(): void {
        this.user = this.authService.getUser();
        this.route.paramMap.subscribe(params => {
            forkJoin([
                this.inspectionService.getByID(params.get('id'))
                    .pipe(
                        tap((inspection: any) => {
                            this.inspection = inspection;
                            this.inspection.inspector_name = this.inspection.user.name;
                            this.inspection.inspector_surname = this.inspection.user.surname;
                            if (this.inspection.referent_name == null) {
                                this.inspection.referent_name = inspection.inspection_orders[0].referent_name;
                            }
                        }),
                        mergeMap((inspection: any) => this.checklistCategoryService.getByChecklistID(inspection.contract.checklist.id_checklist)),
                        switchMap((checklistCategoriesData: any) => from(checklistCategoriesData)),
                        tap((checklistCategory: any) => this.checklistCategories.push(checklistCategory)),
                        mergeMap((checklistCategory: any) => this.checklistSubCategoryService.getByIDCategory(checklistCategory.id_checklist_category)),
                        tap((checklistSubCategories: any) => {
                            this.checklistSubCategories.push(...checklistSubCategories);
                            this.checklistSubCategories.sort((a, b) => {
                                if (a.checklist_category.code < b.checklist_category.code ||
                                    (a.checklist_category.code === b.checklist_category.code && a.code <= b.code)) {
                                    return -1;
                                } else {
                                    return 1;
                                }
                            });
                        })
                    ),
                this.inspectionOrderService.getByIDInspection(params.get('id'))
                    .pipe(
                        switchMap((inspectionOrdersData: any) => from(inspectionOrdersData)),
                        tap((inspectionOrder: any) => this.inspectionOrders.push(inspectionOrder)),
                        mergeMap((inspectionOrder: any) => {
                            return combineLatest(
                                of(inspectionOrder),
                                this.inspectionDeliveryRequestService.getByIDInspectionOrder(inspectionOrder.id_inspection_order)
                            );
                        }),
                        tap(([inspectionOrder, inspectionDeliveryRequests]) => {
                            this.inspectionDeliveryRequestsByOrder[inspectionOrder.id_inspection_order] = inspectionDeliveryRequests;
                        })
                    ),
                this.checklistVoteService.getByIDInspection(params.get('id'))
            ])
                .subscribe({
                    next: (resultData: Array<any>) => {
                        let checklistCategoryLength = 0;
                        let checklistCategoryVotesSum = 0;
                        let checklistCategoryVotesMean = '';
                        let checklistSubCategoryLength = 0;
                        let checklistSubCategoryVotesSum = 0;
                        let checklistSubCategoryVotesMean = '';

                        // Manipolazione dati voti
                        this.inspectionOrders.forEach((inspectionOrder: InspectionOrder) => {
                            this.checklistVotes[inspectionOrder.id_inspection_order!] = {};
                            this.inspectionDeliveryRequestsByOrder[inspectionOrder.id_inspection_order!].forEach((inspectionDeliveryRequest: InspectionDeliveryRequest) => {
                                this.checklistVotes[inspectionOrder.id_inspection_order!][inspectionDeliveryRequest.id_inspection_delivery_request!] = {};
                                this.checklistSubCategories.forEach((checklistSubCategory: ChecklistSubCategory) => {
                                    const checklistVoteFound = resultData[2].find((checklistVote: any) => {
                                        return checklistVote.fk_id_checklist_subcategory === checklistSubCategory.id_checklist_subcategory &&
                                            checklistVote.fk_id_inspection_delivery_request === inspectionDeliveryRequest.id_inspection_delivery_request;
                                    });
                                    this.checklistVotes[inspectionOrder.id_inspection_order!][inspectionDeliveryRequest.id_inspection_delivery_request!][checklistSubCategory.id_checklist_subcategory!] =
                                        (checklistVoteFound)
                                            ? {id: checklistVoteFound.id_checklist_vote, vote: checklistVoteFound.vote}
                                            : {id: null, vote: 'NA'};
                                });
                            });
                        });

                        // Calcolo prospetto punteggi
                        this.inspectionOrders.forEach((inspectionOrder: InspectionOrder) => {
                            this.checklistVotesMeanByCategory[inspectionOrder.id_inspection_order!] = {};
                            // Prospetto punteggi Macro Categorie
                            this.checklistCategories.forEach((checklistCategory, checklistCategoryIndex) => {
                                checklistCategoryLength = 0;
                                checklistCategoryVotesSum = 0;
                                checklistCategoryVotesMean = '';

                                checklistCategory.checklist_subcategories.forEach(checklistSubCategory => {
                                    checklistSubCategoryLength = 0;
                                    checklistSubCategoryVotesSum = 0;
                                    checklistSubCategoryVotesMean = '';

                                    this.inspectionDeliveryRequestsByOrder[inspectionOrder.id_inspection_order!].forEach((inspectionDeliveryRequest: any) => {
                                        let currentChecklistVotes = resultData[2].filter((checklistVote: any) => {
                                            return checklistVote.fk_id_checklist_subcategory === checklistSubCategory.id_checklist_subcategory
                                                && checklistVote.fk_id_inspection_delivery_request === inspectionDeliveryRequest.id_inspection_delivery_request;
                                        });

                                        currentChecklistVotes.forEach((currentChecklistVote: any) => {
                                            if (currentChecklistVote.vote !== 'NA') {
                                                checklistSubCategoryLength++;
                                                checklistSubCategoryVotesSum += +currentChecklistVote.vote;
                                            }
                                        });
                                    });

                                    checklistSubCategoryVotesMean = ((checklistSubCategoryVotesSum !== 0 && checklistSubCategoryLength !== 0) ? (checklistSubCategoryVotesSum / checklistSubCategoryLength).toFixed(1) : 'NA').replace(".", ",");
                                    if (checklistSubCategoryVotesMean !== 'NA') {
                                        checklistCategoryLength++;
                                        checklistCategoryVotesSum += parseFloat(checklistSubCategoryVotesMean.replace(",", "."));
                                    }
                                });

                                checklistCategoryVotesMean = ((checklistCategoryVotesSum !== 0 && checklistCategoryLength !== 0) ? (checklistCategoryVotesSum / checklistCategoryLength).toFixed(1) : 'NA').replace(".", ",");
                                this.checklistVotesMeanByCategory[inspectionOrder.id_inspection_order!][checklistCategory.id_checklist_category!] = checklistCategoryVotesMean;
                            });
                        });
                    },
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });

            this.documentService.getByIDInspection(params.get('id'))
                .subscribe({
                    next: (documents: any) => {
                        this.inspectionDocuments = documents;
                    },
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });
        });
    }

    downloadInspectionDocument(inspectionDocument: Document) {
        this.documentService.download(inspectionDocument.fk_id_inspection.toString(), inspectionDocument.name)
            .subscribe({
                next: (blobFile) => {
                    saveAs(blobFile, inspectionDocument.name);
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    openUploadInspectionDocumentModal() {
        const modalRef = this.modalService.open(InspectionDocumentUploadComponent);
        modalRef.componentInstance.inspection = this.inspection;
        modalRef.result.then(() => {
            this.documentService.getByIDInspection(this.inspection.id_inspection!.toString())
                .subscribe({
                    next: (documents: any) => {
                        this.inspectionDocuments = documents;
                    },
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });
        });
    }

    openDeleteInspectionDocumentModal(inspectionDocument: Document) {
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = 'Rimozione <strong>Documento</strong>';
        modalRef.componentInstance.text = 'Confermi la rimozione del documento con titolo "' + inspectionDocument.name + '"?';
        modalRef.result.then(() => {
            if (inspectionDocument.id_document) {
                this.documentService.delete(inspectionDocument.id_document)
                    .subscribe({
                        next: () => {
                            this.inspectionDocuments = this.inspectionDocuments.filter((currInspectionDocument: Document) =>
                                currInspectionDocument.id_document !== inspectionDocument.id_document);
                        },
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        });
    }

    validateChecklist() {
        this.inspection.fk_id_inspection_state = 6;
        this.inspection.date_validation = new Date();
        this.inspectionService.update(this.inspection)
            .subscribe({
                next: () => {
                    this.router.navigate(['/inspection']);
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    generateReport() {
        this.loadingOverlay = true;
        this.inspectionService.generateReport(this.inspection.id_inspection!.toString())
            .subscribe({
                next: (blobFile) => {
                    const currentDate = new Date();
                    const currentFormattedDate = currentDate.getDate().toString().padStart(2, '0') + "-" +
                        (currentDate.getMonth() + 1).toString().padStart(2, '0') + "-" +
                        currentDate.getFullYear();
                    saveAs(blobFile, "Rapporto Ispezione " + this.inspection.pa.name + " - " + currentFormattedDate + ".pdf");
                    this.loadingOverlay = false;
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                    this.loadingOverlay = false;
                }
            });
    }

    saveChecklistVotes() {
        this.isButtonEnabled = false;
        let checklistVotesToSave: Array<any> = [];

        this.inspectionOrders.forEach((inspectionOrder: InspectionOrder) => {
            this.inspectionDeliveryRequestsByOrder[inspectionOrder.id_inspection_order!].forEach((inspectionDeliveryRequest: InspectionDeliveryRequest) => {
                this.checklistSubCategories.forEach((checklistSubCategory: ChecklistSubCategory) => {
                    checklistVotesToSave.push({
                        id_checklist_vote: this.checklistVotes[inspectionOrder.id_inspection_order!][inspectionDeliveryRequest.id_inspection_delivery_request!][checklistSubCategory.id_checklist_subcategory!]['id'],
                        fk_id_checklist_subcategory: checklistSubCategory.id_checklist_subcategory,
                        fk_id_contract: this.inspection.fk_id_contract,
                        fk_id_inspection: this.inspection.id_inspection,
                        fk_id_inspection_delivery_request: inspectionDeliveryRequest.id_inspection_delivery_request,
                        vote: this.checklistVotes[inspectionOrder.id_inspection_order!][inspectionDeliveryRequest.id_inspection_delivery_request!][checklistSubCategory.id_checklist_subcategory!]['vote']
                    });
                });
            });
        });

        let inspectionOrderUpdated = 0;
        this.checklistVoteService.upsertMassive(checklistVotesToSave)
            .pipe(
                tap(() => this.inspection.fk_id_inspection_state = 5),
                mergeMap(() => this.inspectionService.update(this.inspection))
            )
            .subscribe({
                next: () => {
                    this.inspectionOrders.forEach((inspectionOrder: InspectionOrder) => {
                       this.inspectionOrderService.update(inspectionOrder)
                           .subscribe({
                               next: async () => {
                                   inspectionOrderUpdated++;
                                   window.location.reload();
                               },
                               error: async (err: HttpErrorResponse) => {
                                   console.log(err);
                                   window.location.reload();
                               }
                           });
                    });
                },
                error: async (err: HttpErrorResponse) => {
                    console.log(err);
                    window.location.reload();
                }
            });
    }

}
